// Native in-browser Anki .apkg generator (sql.js + JSZip).
// Builds a real Anki collection with "::" hierarchical sub-decks.
import initSqlJs from "sql.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";

let sqlPromise = null;
function getSql() {
  if (!sqlPromise) {
    const pub = process.env.PUBLIC_URL || "";
    sqlPromise = initSqlJs({ locateFile: (f) => `${pub}/${f}` }).catch((e) => {
      sqlPromise = null; // allow retry on next attempt
      throw e;
    });
  }
  return sqlPromise;
}

async function sha1Int(str) {
  const buf = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(str));
  const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  return parseInt(hex.substring(0, 8), 16);
}

function guid() {
  const c = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let s = "";
  for (let i = 0; i < 10; i++) s += c[Math.floor(Math.random() * c.length)];
  return s;
}

const SCHEMA = `
CREATE TABLE col (id integer primary key, crt integer not null, mod integer not null, scm integer not null, ver integer not null, dty integer not null, usn integer not null, ls integer not null, conf text not null, models text not null, decks text not null, dconf text not null, tags text not null);
CREATE TABLE notes (id integer primary key, guid text not null, mid integer not null, mod integer not null, usn integer not null, tags text not null, flds text not null, sfld integer not null, csum integer not null, flags integer not null, data text not null);
CREATE TABLE cards (id integer primary key, nid integer not null, did integer not null, ord integer not null, mod integer not null, usn integer not null, type integer not null, queue integer not null, due integer not null, ivl integer not null, factor integer not null, reps integer not null, lapses integer not null, left integer not null, odue integer not null, odid integer not null, flags integer not null, data text not null);
CREATE TABLE revlog (id integer primary key, cid integer not null, usn integer not null, ease integer not null, ivl integer not null, lastIvl integer not null, factor integer not null, time integer not null, type integer not null);
CREATE TABLE graves (usn integer not null, oid integer not null, type integer not null);
CREATE INDEX ix_notes_usn on notes (usn);
CREATE INDEX ix_cards_usn on cards (usn);
CREATE INDEX ix_cards_nid on cards (nid);
CREATE INDEX ix_cards_sched on cards (did, queue, due);
CREATE INDEX ix_revlog_usn on revlog (usn);
CREATE INDEX ix_notes_csum on notes (csum);
CREATE INDEX ix_revlog_cid on revlog (cid);
`;

function deckObj(id, name, crt) {
  return { id, name, mod: crt, usn: -1, lrnToday: [0, 0], revToday: [0, 0], newToday: [0, 0],
    timeToday: [0, 0], collapsed: false, browserCollapsed: false, desc: "", dyn: 0, conf: 1,
    extendNew: 10, extendRev: 50 };
}

const DCONF = {
  "1": { id: 1, mod: 0, name: "Default", usn: 0, maxTaken: 60, autoplay: true, timer: 0, replayq: true,
    new: { bury: false, delays: [1, 10], initialFactor: 2500, ints: [1, 4, 0], order: 1, perDay: 20 },
    rev: { bury: false, ease4: 1.3, ivlFct: 1, maxIvl: 36500, perDay: 200, hardFactor: 1.2 },
    lapse: { delays: [10], leechAction: 1, leechFails: 8, minInt: 1, mult: 0 }, dyn: false },
};

// notes: [{ front, back, deck }]
async function buildApkg(notes, filename) {
  const SQL = await getSql();
  const db = new SQL.Database();
  db.run(SCHEMA);

  const now = Date.now();
  const crt = Math.floor(now / 1000);
  const modelId = 1607392319000;

  const models = {
    [modelId]: {
      id: modelId, name: "FractalNotes Basic", type: 0, mod: crt, usn: -1, sortf: 0, did: 1,
      tmpls: [{ name: "Card 1", ord: 0, qfmt: "{{Front}}", afmt: "{{FrontSide}}\n\n<hr id=answer>\n\n{{Back}}", did: null, bqfmt: "", bafmt: "" }],
      flds: [
        { name: "Front", ord: 0, sticky: false, rtl: false, font: "Arial", size: 20, media: [] },
        { name: "Back", ord: 1, sticky: false, rtl: false, font: "Arial", size: 20, media: [] },
      ],
      css: ".card{font-family:arial;font-size:20px;text-align:center;color:black;background:white;}",
      latexPre: "\\documentclass[12pt]{article}\n\\usepackage{amssymb,amsmath}\n\\pagestyle{empty}\n\\begin{document}",
      latexPost: "\\end{document}", req: [[0, "all", [0]]], vers: [], tags: [],
    },
  };

  const deckNames = [...new Set(notes.map((n) => n.deck))];
  const decks = { "1": deckObj(1, "Default", crt) };
  const deckIdByName = {};
  deckNames.forEach((name, i) => {
    const id = 1600000000000 + i;
    deckIdByName[name] = id;
    decks[id] = deckObj(id, name, crt);
  });

  const conf = { nextPos: 1, estTimes: true, activeDecks: [1], sortType: "noteFld", timeLim: 0,
    sortBackwards: false, addToCur: true, curDeck: 1, newSpread: 0, dueCounts: true,
    curModel: String(modelId), collapseTime: 1200 };

  db.run("INSERT INTO col VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?)", [
    1, crt, now, now, 11, 0, 0, 0,
    JSON.stringify(conf), JSON.stringify(models), JSON.stringify(decks), JSON.stringify(DCONF), "{}",
  ]);

  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    const noteId = now + i;
    const cardId = now + 500000 + i;
    const flds = `${n.front}\u001f${n.back}`;
    const csum = await sha1Int(n.front);
    db.run("INSERT INTO notes VALUES (?,?,?,?,?,?,?,?,?,?,?)", [
      noteId, guid(), modelId, crt, -1, "", flds, n.front, csum, 0, "",
    ]);
    db.run("INSERT INTO cards VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)", [
      cardId, noteId, deckIdByName[n.deck], 0, crt, -1, 0, 0, i + 1, 0, 0, 0, 0, 0, 0, 0, 0, "",
    ]);
  }

  const data = db.export();
  db.close();

  const zip = new JSZip();
  zip.file("collection.anki2", data);
  zip.file("media", "{}");
  const blob = await zip.generateAsync({ type: "blob" });
  if (!blob || blob.size < 100) throw new Error("apkg build produced empty file");
  saveAs(blob, filename);
}

function deckName(pathArr) {
  return pathArr.map((p) => String(p).replace(/::/g, "-")).join("::");
}

export async function exportCurrentLayer(grid) {
  const deck = deckName(grid.path);
  const notes = grid.elements.map((el) => ({ front: el.flashcard_question, back: el.flashcard_answer, deck }));
  await buildApkg(notes, `${(grid.path[0] || "FractalNotes").replace(/[^\w]/g, "_")}_layer.apkg`);
}

export async function exportMasterTree(cacheEntries, rootName) {
  const notes = [];
  cacheEntries.forEach((grid) => {
    const deck = deckName(grid.path);
    grid.elements.forEach((el) => notes.push({ front: el.flashcard_question, back: el.flashcard_answer, deck }));
  });
  await buildApkg(notes, `${(rootName || "FractalNotes").replace(/[^\w]/g, "_")}_master_tree.apkg`);
}
